import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faTimes } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

/**
 * MAX_BULK_IPS / PREVIEW_LIMIT
 *
 * Configuration for maximum allowed bulk IPs and how many to show in the
 * quick preview area.
 */
const MAX_BULK_IPS = 100;
const PREVIEW_LIMIT = 10;

/**
 * IpBulkForm
 *
 * Modal form that allows pasting/uploading multiple IP addresses in a
 * single batch. It validates each address, shows a preview (with a
 * maximum preview limit), and constructs an array payload for `onSave`.
 */
const IpBulkForm = ({ onSave, onCancel, loading = false }) => {
  const { t } = useTranslation();
  const strings = t('ipAddresses.forms.bulk', { returnObjects: true });
  const requiredIndicator = t('common.form.requiredIndicator');
  const [formData, setFormData] = useState({
    ip_addresses: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [previewIps, setPreviewIps] = useState([]);

  /**
   * validateIpAddress(ip)
   *
   * Validates a single IPv4 address using a strict regex. Returns boolean.
   */
  const validateIpAddress = (ip) => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip.trim());
  };

  /**
   * parseIpAddresses(input)
   *
   * Splits the input text by newlines, commas or whitespace and returns a
   * trimmed array of IP address strings.
   */
  const parseIpAddresses = (input) => {
    if (!input || !input.trim()) return [];
    const ips = input
      .split(/[\n,\s]+/)
      .map(ip => ip.trim())
      .filter(ip => ip.length > 0);
    return ips;
  };

  /**
   * validateForm()
   *
   * Validates the bulk IPs textarea and the description field. Ensures
   * each IP is valid and the count limits are respected. Returns boolean.
   */
  const validateForm = () => {
    const newErrors = {};
    const ips = parseIpAddresses(formData.ip_addresses);

    if (!formData.ip_addresses || !formData.ip_addresses.trim()) {
      newErrors.ip_addresses = strings.fields.ipAddresses.errors.required;
    } else if (ips.length === 0) {
      newErrors.ip_addresses = strings.fields.ipAddresses.errors.noneValid;
    } else {
      const invalidIps = ips.filter(ip => !validateIpAddress(ip));
      if (invalidIps.length > 0) {
        newErrors.ip_addresses = t('ipAddresses.forms.bulk.fields.ipAddresses.errors.invalidList', { ips: invalidIps.join(', ') });
      } else if (ips.length > MAX_BULK_IPS) {
        newErrors.ip_addresses = t('ipAddresses.forms.bulk.fields.ipAddresses.errors.limitExceeded', { max: MAX_BULK_IPS });
      }
    }

    if (!formData.description || !formData.description.trim()) {
      newErrors.description = strings.fields.description.errors.required;
    } else if (formData.description.trim().length < 5) {
      newErrors.description = strings.fields.description.errors.minLength;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * handleInputChange(e)
   *
   * Generic input handler that also updates the preview when the
   * `ip_addresses` textarea changes.
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    if (name === 'ip_addresses') {
      setPreviewIps(parseIpAddresses(value));
    }
  };

  /**
   * handleSubmit(e)
   *
   * Prevents default submit, validates and calls `onSave` with the parsed
   * IP addresses and description.
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const ips = parseIpAddresses(formData.ip_addresses);
    const submitData = { ip_addresses: ips, description: formData.description.trim() };
    onSave(submitData);
  };

  /**
   * handlePasteExample()
   *
   * Copies a sample list of IP addresses into the textarea for convenience
   * during testing or demos.
   */
  const handlePasteExample = () => {
    const exampleIps = strings.examples.ipList;
    setFormData(prev => ({ ...prev, ip_addresses: exampleIps }));
    setPreviewIps(parseIpAddresses(exampleIps));
  };

  const previewCount = previewIps.length;
  const previewTitle = t('ipAddresses.forms.bulk.preview.title', { count: previewCount });
  const previewTruncatedLabel = t('ipAddresses.forms.bulk.preview.truncated', { count: PREVIEW_LIMIT });
  const previewMoreLabel = t('ipAddresses.forms.bulk.preview.more', { count: Math.max(previewCount - PREVIEW_LIMIT, 0) });
  const previewSlice = previewIps.slice(0, PREVIEW_LIMIT);
  const showPreviewMore = previewCount > PREVIEW_LIMIT;
  const submitButtonLabel = previewCount > 0
    ? t('ipAddresses.forms.bulk.buttons.submit', { count: previewCount })
    : strings.buttons.submitFallback;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
    >
      <style>{`.ip-bulk-modal{ -ms-overflow-style: none; scrollbar-width: none; } .ip-bulk-modal::-webkit-scrollbar{ display: none; }`}</style>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto ip-bulk-modal"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>
              <FontAwesomeIcon icon={faUpload} className="text-lg" />
            </div>
            <div>
              <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{strings.title}</h2>
              <p className="mt-1 text-base" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{strings.subtitle}</p>
            </div>
          </div>

          <button
            onClick={onCancel}
            className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
            style={{
              color: UI_COLORS.TEXT.MUTED,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1)
            }}
            onMouseEnter={(e) => { e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2); }}
            onMouseLeave={(e) => { e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1); }}
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* IP Addresses Field */}
          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="ip_addresses" className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {strings.fields.ipAddresses.label} <span style={{ color: UI_COLORS.STATUS.ERROR }}>{requiredIndicator}</span>
              </label>
              <button type="button" onClick={handlePasteExample} className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
                {strings.fields.ipAddresses.actions.pasteExample}
              </button>
            </div>

            <textarea
              id="ip_addresses"
              name="ip_addresses"
              rows="8"
              value={formData.ip_addresses}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:ring-2 ${errors.ip_addresses ? 'border-red-300 focus:border-red-500' : 'focus:border-transparent'}`}
              style={{
                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                borderColor: errors.ip_addresses ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                color: UI_COLORS.FORMS.INPUT.text,
                '--tw-ring-color': colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.3)
              }}
              placeholder={strings.fields.ipAddresses.placeholder}
              disabled={loading}
            />
            {errors.ip_addresses && (
              <p className="mt-2 text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.ip_addresses}</p>
            )}

            {/* Preview */}
            {previewCount > 0 && (
              <div className="mt-3 p-3 rounded-md" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.SUBTLE }}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{previewTitle}</span>
                  {showPreviewMore && (
                    <span className="text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{previewTruncatedLabel}</span>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {previewSlice.map((ip, index) => (
                    <span key={index} className="inline-flex px-2 py-1 text-xs rounded" style={{ backgroundColor: validateIpAddress(ip) ? colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.12) : colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.12), color: validateIpAddress(ip) ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.ERROR }}>
                      {ip}
                    </span>
                  ))}
                  {showPreviewMore && (
                    <span className="inline-flex px-2 py-1 text-xs rounded" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.SUBTLE, color: UI_COLORS.TEXT.SECONDARY }}>{previewMoreLabel}</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Description Field */}
          <div>
            <label htmlFor="description" className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {strings.fields.description.label} <span style={{ color: UI_COLORS.STATUS.ERROR }}>{requiredIndicator}</span>
            </label>
            <input
              type="text"
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:ring-2 ${errors.description ? 'border-red-300 focus:border-red-500' : 'focus:border-transparent'}`}
              style={{
                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                borderColor: errors.description ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                color: UI_COLORS.FORMS.INPUT.text,
                '--tw-ring-color': colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.3)
              }}
              placeholder={strings.fields.description.placeholder}
              disabled={loading}
            />
            {errors.description && (
              <p className="mt-2 text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.description}</p>
            )}
            <p className="mt-1 text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{strings.fields.description.helper}</p>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:transform-none"
              style={{
                backgroundColor: 'transparent',
                color: UI_COLORS.TEXT.MUTED,
                border: `2px solid ${UI_COLORS.BORDERS.MEDIUM}`
              }}
              onMouseEnter={(e) => { if (!loading) { e.target.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1); } }}
              onMouseLeave={(e) => { if (!loading) { e.target.style.backgroundColor = 'transparent'; } }}
            >
              {strings.buttons.cancel}
            </button>

            <button
              type="submit"
              className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:transform-none"
              style={{
                backgroundColor: loading ? UI_COLORS.BUTTONS.PRIMARY.loading.background : UI_COLORS.BUTTONS.PRIMARY.background,
                color: loading ? UI_COLORS.BUTTONS.PRIMARY.loading.text : UI_COLORS.BUTTONS.PRIMARY.text
              }}
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{strings.buttons.loading}</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faUpload} />
                  <span>{submitButtonLabel}</span>
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IpBulkForm;